import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import giftReward from "../../assets/images/cp/RewardBg.png";
import { useTranslation } from "react-i18next";
import TimerComponent from "./TimerComponent";
import fallbackImage from "../../assets/icons/selected_male.png";
import fallbackImage1 from "../../assets/icons/selected_female.png";
import fallbackImage2 from "../../assets/icons/gift-box 1.png";
import DialogButton from "../../assets/images/cp/DialogButton.png";
import activeTabBar1 from "../../assets/images/cp/active.png";
import tabBar1 from "../../assets/images/cp/notactive.png";
import flowers from "../../assets/images/cp/flowers.webp";
import topHeartBg from "../../assets/images/cp/topHeartBg.png";
import topHeartBg2 from "../../assets/images/cp/topThreeBg4.png";
import topHeartBg3 from "../../assets/images/cp/topThreeBg3.png";
import TopTitle from "../../assets/images/cp/TopTitle.png";
import TopTitle2 from "../../assets/images/cp/TopTitle2.png";
import TopTitle3 from "../../assets/images/cp/TopTitle3.png";
import heart from "../../assets/images/cp/heart.png";
import ringTop2 from "../../assets/images/cp/top2.webp";
import ringTop22 from "../../assets/images/cp/second2.webp";
import middleHeart from "../../assets/images/cp/timer.png";
import ringTop1 from "../../assets/images/cp/top1.webp";
import ringTop11 from "../../assets/images/cp/second1.webp";
import Dialog from "./Dialog";

const Design = ({ CpTopRan, AllEvents }) => {
  const [activeTab, setActiveTab] = useState("tab1");

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);
  const { t } = useTranslation();
  const [imageErrors, setImageErrors] = useState({});
  const ImageBaseUrl = "https://media.sh8afchat.com/";

  // Handle image error by updating the imageErrors state
  const handleError = (itemId) => {
    setImageErrors((prevState) => ({
      ...prevState,
      [itemId]: true, // Mark this item image as failed to load
    }));
  };

  const RewardsSection = () => {
    const [hoveredCard, setHoveredCard] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [scrollPositions, setScrollPositions] = useState({});

    const scrollRefs = useRef([]);

    // Animation variants
    const containerVariants = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.15,
          delayChildren: 0.2,
        },
      },
    };

    const cardVariants = {
      hidden: {
        opacity: 0,
        y: 60,
        scale: 0.9,
        rotateX: -15,
      },
      visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        rotateX: 0,
        transition: {
          type: "spring",
          damping: 15,
          stiffness: 100,
          duration: 0.8,
        },
      },
    };

    const rewardConfig = [
      {
        rank: 1,
        className: "Reward2",
        title: "🏆 GOLD",
        subtitle: "CHAMPION",
        gradient: "from-amber-300 via-yellow-400 to-amber-500",
        glowColor: "amber",
        textColor: "text-[#bd2561]",
        bgPattern:
          "radial-gradient(circle at 30% 30%, rgba(251, 191, 36, 0.15), transparent 70%)",
        data: CpTopRan?.target_rewards?.top_1,
      },
      {
        rank: 2,
        className: "Reward1",
        title: "⚡ SILVER",
        subtitle: "ELITE",
        gradient: "from-gray-300 via-gray-400 to-gray-500",
        glowColor: "gray",
        textColor: "text-[#bd2561]",
        bgPattern:
          "radial-gradient(circle at 70% 30%, rgba(156, 163, 175, 0.15), transparent 70%)",
        data: CpTopRan?.target_rewards?.top_2,
      },
      {
        rank: 3,
        className: "Reward3",
        title: "✨ BRONZE",
        subtitle: "RISING",
        gradient: "from-amber-600 via-amber-700 to-amber-800",
        glowColor: "amber",
        textColor: "text-[#bd2561]",
        bgPattern:
          "radial-gradient(circle at 50% 70%, rgba(180, 83, 9, 0.15), transparent 70%)",
        data: CpTopRan?.target_rewards?.top_3,
      },
    ];

    const handleScroll = (index, e) => {
      setScrollPositions((prev) => ({
        ...prev,
        [index]: e.target.scrollLeft,
      }));
    };

    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="tab-panel mx-auto mt-[-40px] w-[100%] max-w-7xl px-4"
      >
        {/* Decorative background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [0, -30, 0],
                x: [0, i % 2 === 0 ? 20 : -20, 0],
                rotate: [0, 180, 360],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 10 + i * 2,
                repeat: Infinity,
                ease: "linear",
              }}
              className="absolute w-32 h-32 bg-gradient-to-r from-amber-500/5 to-purple-500/5 rounded-full blur-3xl"
              style={{
                left: `${i * 20}%`,
                top: `${i * 15}%`,
              }}
            />
          ))}
        </div>

        {/* Main rewards grid */}
        <div className="flex flex-col lg:flex-row justify-center items-center gap-8 lg:gap-6 relative z-10">
          {rewardConfig.map((reward, cardIndex) => (
            <motion.div
              key={cardIndex}
              variants={cardVariants}
              whileHover={{
                scale: 1.03,
                transition: { type: "spring", stiffness: 300 },
              }}
              onHoverStart={() => setHoveredCard(cardIndex)}
              onHoverEnd={() => setHoveredCard(null)}
              className="relative w-full max-w-[380px] perspective-1000"
            >
              {/* 3D Card Container */}
              <motion.div
                animate={{
                  rotateY: hoveredCard === cardIndex ? 5 : 0,
                  rotateX: hoveredCard === cardIndex ? 5 : 0,
                }}
                transition={{ type: "spring", stiffness: 300 }}
                className="relative transform-gpu"
                style={{ transformStyle: "preserve-3d" }}
              >
                {/* Floating particles specific to card */}
                <div className="absolute inset-0 overflow-hidden rounded-3xl">
                  {[...Array(8)].map((_, i) => (
                    <motion.div
                      key={i}
                      animate={{
                        y: [0, -40, 0],
                        x: [0, i % 2 === 0 ? 20 : -20, 0],
                        opacity: [0, 0.4, 0],
                        scale: [0, 1, 0],
                      }}
                      transition={{
                        duration: 4 + Math.random() * 3,
                        repeat: Infinity,
                        delay: i * 0.5 + cardIndex * 0.3,
                        ease: "easeInOut",
                      }}
                      className={`absolute w-1.5 h-1.5 rounded-full ${
                        cardIndex === 0
                          ? "bg-yellow-300"
                          : cardIndex === 1
                            ? "bg-gray-300"
                            : "bg-amber-500"
                      }`}
                      style={{
                        left: `${20 + Math.random() * 60}%`,
                        top: `${20 + Math.random() * 60}%`,
                      }}
                    />
                  ))}
                </div>

                {/* Main Card with gradient overlay */}
                <div className={`relative ${reward.className} group/card`}>
                  {/* Animated gradient overlay */}
                  <motion.div
                    animate={{
                      opacity: [0.3, 0.5, 0.3],
                      scale: [1, 1.05, 1],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      delay: cardIndex * 0.2,
                    }}
                    className={`absolute inset-0   rounded-3xl`}
                  />

                  {/* Card shine effect */}
                  <motion.div
                    animate={{
                      x: ["-100%", "200%"],
                    }}
                    transition={{
                      duration: 2.5,
                      repeat: Infinity,
                      delay: cardIndex * 0.3,
                      ease: "easeInOut",
                    }}
                    className="absolute inset-0     pointer-events-none"
                  />

                  {/* Rewards Container */}
                  <div className="relative pt-20 pb-6 px-4">
                    {/* Rewards scroll area */}
                    <div
                      ref={(el) => (scrollRefs.current[cardIndex] = el)}
                      onScroll={(e) => handleScroll(cardIndex, e)}
                      className={`flex gap-3 overflow-x-auto  overflow-y-hidden pb-4 hide-scrollbar scroll-smooth ${
                        reward.data?.length <= 3
                          ? "justify-center"
                          : "justify-start"
                      }`}
                      style={{
                        maskImage:
                          reward.data?.length > 3
                            ? "linear-gradient(90deg, transparent, black 20px, black calc(100% - 40px), transparent)"
                            : "none",
                        WebkitMaskImage:
                          reward.data?.length > 3
                            ? "linear-gradient(90deg, transparent, black 20px, black calc(100% - 40px), transparent)"
                            : "none",
                      }}
                    >
                      <AnimatePresence mode="wait">
                        {reward.data && reward.data.length > 0 ? (
                          reward.data.map((item, itemIndex) => (
                            <motion.div
                              key={itemIndex}
                              initial={{ opacity: 0, scale: 0.5, rotate: -15 }}
                              animate={{
                                opacity: 1,
                                scale: 1,
                                rotate: 0,
                                transition: {
                                  delay: itemIndex * 0.1,
                                  type: "spring",
                                  stiffness: 200,
                                },
                              }}
                              whileHover={{
                                scale: 1.15,
                                rotate: 5,
                                y: -8,
                                transition: { type: "spring", stiffness: 400 },
                              }}
                              onClick={() => setSelectedItem(item)}
                              className="relative flex-shrink-0 w-[85px] cursor-pointer group/item"
                            >
                              {/* Reward card with floating animation */}
                              <motion.div
                                animate={{
                                  y: [0, -3, 0],
                                }}
                                transition={{
                                  duration: 2,
                                  repeat: Infinity,
                                  delay: itemIndex * 0.1,
                                }}
                                className="relative"
                              >
                                {/* Glow ring on hover */}
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  whileHover={{
                                    opacity: 1,
                                    scale: 1.2,
                                    transition: { duration: 0.2 },
                                  }}
                                  className={`absolute inset-0 bg-gradient-to-r ${reward.gradient} rounded-full blur-xl`}
                                />

                                {/* Gift box with rotating animation */}
                                <div className="relative w-full aspect-square">
                                  <motion.img
                                    animate={{
                                      rotate: [0, 5, -5, 0],
                                    }}
                                    transition={{
                                      duration: 3,
                                      repeat: Infinity,
                                      delay: itemIndex * 0.2,
                                    }}
                                    src={giftReward}
                                    alt="Gift background"
                                    className="absolute inset-0 w-[90%] h-[90%] m-auto  object-contain drop-shadow-2xl"
                                  />

                                  {/* Reward image with 3D effect */}
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <motion.div
                                      whileHover={{
                                        rotate: 360,
                                        scale: 1.1,
                                      }}
                                      transition={{ duration: 0.5 }}
                                      className="relative"
                                    >
                                      <img
                                        className="w-14 h-14 rounded-xl object-cover shadow-2xl border-2 border-white/30"
                                        src={
                                          imageErrors[item.image]
                                            ? fallbackImage2
                                            : `${ImageBaseUrl}${item.image}`
                                        }
                                        onError={() => handleError(item.image)}
                                        alt={item?.name || "Reward"}
                                      />

                                      {/* Corner sparkles */}
                                      <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{
                                          duration: 4,
                                          repeat: Infinity,
                                          ease: "linear",
                                        }}
                                        className="absolute -top-1 -right-1 w-2 h-2"
                                      >
                                        <div className="w-full h-full bg-yellow-300 rounded-full blur-[1px]" />
                                      </motion.div>
                                    </motion.div>
                                  </div>
                                </div>

                                {/* Reward name with animated underline */}
                                <div className="relative mt-2">
                                  <motion.p
                                    whileHover={{ scale: 1.05 }}
                                    className={`${reward.textColor} text-[12px] font-medium text-center truncate`}
                                  >
                                    {item?.name}
                                  </motion.p>
                                  <motion.div
                                    initial={{ width: 0 }}
                                    whileHover={{ width: "100%" }}
                                    className={`absolute bottom-0 left-0 h-px bg-gradient-to-r ${reward.gradient}`}
                                  />
                                </div>

                                {/* Tooltip on hover */}
                                <motion.div
                                  initial={{ opacity: 0, y: 10, scale: 0.9 }}
                                  whileHover={{ opacity: 1, y: 0, scale: 1 }}
                                  className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-black/90 backdrop-blur-sm text-white text-[10px] px-2 py-1 rounded-full whitespace-nowrap border border-white/20 z-50"
                                >
                                  {item?.name}
                                </motion.div>
                              </motion.div>
                            </motion.div>
                          ))
                        ) : (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [0.3, 0.7, 0.3] }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="flex items-center justify-center w-full py-6"
                          >
                            <p className="text-xs font-medium text-white/50">
                              {t("no data available")}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Bottom decorative element */}
                  <motion.div
                    animate={{ scaleX: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className={`absolute bottom-2 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r ${reward.gradient} rounded-full blur-sm opacity-50`}
                  />
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Reward Details Modal */}
        <AnimatePresence>
          {selectedItem && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 backdrop-blur-xl flex items-center justify-center z-50 p-4"
              onClick={() => setSelectedItem(null)}
            >
              <motion.div
                initial={{ scale: 0.8, y: 50, rotateX: -15 }}
                animate={{ scale: 1, y: 0, rotateX: 0 }}
                exit={{ scale: 0.8, y: 50, rotateX: 15 }}
                transition={{ type: "spring", damping: 20 }}
                className="relative max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-purple-500 rounded-3xl blur-2xl opacity-30" />

                {/* Modal content */}
                <div className="relative bg-gradient-to-br from-[#2a1a0a] to-[#1a0f05] p-8 rounded-3xl border-2 border-amber-500/30 shadow-2xl">
                  {/* Floating particles */}
                  {[...Array(6)].map((_, i) => (
                    <motion.div
                      key={i}
                      animate={{
                        y: [0, -20, 0],
                        x: [0, i % 2 === 0 ? 10 : -10, 0],
                        opacity: [0, 0.5, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: i * 0.3,
                      }}
                      className="absolute w-1 h-1 bg-amber-400 rounded-full"
                      style={{
                        left: `${20 + Math.random() * 60}%`,
                        top: `${20 + Math.random() * 60}%`,
                      }}
                    />
                  ))}

                  {/* Reward image */}
                  <motion.img
                    initial={{ scale: 0.5 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.2 }}
                    src={
                      imageErrors[selectedItem.image]
                        ? fallbackImage2
                        : `${ImageBaseUrl}${selectedItem.image}`
                    }
                    className="w-40 h-40 mx-auto rounded-2xl object-cover mb-6 border-4 border-amber-400 shadow-2xl"
                    alt={selectedItem.name}
                  />

                  {/* Reward details */}
                  <motion.h3
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-amber-300 font-bold text-2xl mb-2 text-center"
                  >
                    {selectedItem.name}
                  </motion.h3>

                  <motion.p
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="text-amber-200/70 text-sm mb-6 text-center"
                  >
                    {selectedItem.description || "Special reward item"}
                  </motion.p>

                  {/* Close button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedItem(null)}
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-[#421404] font-bold py-3 rounded-xl shadow-lg relative overflow-hidden group"
                  >
                    <span className="relative z-10">Close</span>
                    <motion.div
                      animate={{
                        x: ["-100%", "100%"],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                    />
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  // Add these styles to your CSS
  const styles = `
  .Reward1, .Reward2, .Reward3 {
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    width: 100%;
    min-height: 280px;
    border-radius: 24px;
    position: relative;
    transition: all 0.3s ease;
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.4),
      inset 0 1px 1px rgba(255, 255, 255, 0.1);
  }

  .Reward1 {
    background-image: url("../src/assets/images/cp/Reward1.png");
  }

  .Reward2 {
    background-image: url("../src/assets/images/cp/Reward2.png");
  }

  .Reward3 {
    background-image: url("../src/assets/images/cp/Reward3.png");
  }

  .perspective-1000 {
    perspective: 1000px;
  }

  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
  
  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  @media (min-width: 1024px) {
    .Reward1, .Reward2, .Reward3 {
      width: 350px;
    }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-5px); }
  }
`;
  // TopThreeCard Component
  const TopThreeCard = ({
    data,
    rank,
    imageErrors,
    fallbackImage,
    fallbackImage1,
    ImageBaseUrl,
    handleError,
    ringTop1,
    ringTop2,
    middleHeart,
    topHeartBg,
    TopTitle,
    heart,
  }) => {
    const getRankColors = (rank) => {
      switch (rank) {
        case 1:
          return {
            bg: "from-yellow-400 to-orange-500",
            text: "text-yellow-400",
            border: "border-yellow-400",
            shadow: "shadow-yellow-400/50",
            ring: "ring-yellow-400",
            height: "h-[100px]",
            width: "w-[100px]",
            height1: "h-[80px]",
            width1: "w-[80px]",
          };
        case 2:
          return {
            bg: "from-gray-400 to-gray-500",
            text: "text-gray-400",
            border: "border-gray-400",
            shadow: "shadow-gray-400/50",
            ring: "ring-gray-400",
            height: "h-[80px]",
            width: "w-[80px]",
            height1: "h-[60px]",
            width1: "w-[60px]",
          };
        case 3:
          return {
            bg: "from-orange-400 to-red-500",
            text: "text-orange-400",
            border: "border-orange-400",
            shadow: "shadow-orange-400/50",
            ring: "ring-orange-400",
            height: "h-[80px]",
            width: "w-[80px]",
            height1: "h-[60px]",
            width1: "w-[60px]",
          };
        default:
          return {
            bg: "from-purple-400 to-pink-500",
            text: "text-purple-400",
            border: "border-purple-400",
            shadow: "shadow-purple-400/50",
            ring: "ring-purple-400",
            height: "h-[80px]",
            width: "w-[80px]",
            height1: "h-[60px]",
            width1: "w-[60px]",
          };
      }
    };

    const colors = getRankColors(rank);

    return (
      <div className="relative w-full rounded-2xl    ">
        {/* TopHeartBg as background image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat    transition-opacity duration-500"
          style={{
            backgroundImage: `url(${topHeartBg})`,
            height: "230px",
            backgroundSize: "contain",
          }}
        />

        {/* Main content */}
        <div className="relative z-10 top-[42px]">
          {/* Couple Images with Rings - Adjusted spacing for compactness */}
          <div className="relative flex justify-center items-center gap-1 ">
            {/* Left Ring and Image */}
            <div className="relative group/image">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-white/20 to-transparent blur-md opacity-0 group-hover/image:opacity-100 transition-opacity duration-300" />
              <img
                src={data.sender_cp_gender === 0 ? ringTop2 : ringTop1}
                alt="ring"
                className={`relative z-10 ${colors.height} ${colors.width} z-50 animate-spin-slow ${
                  rank === 1 ? "opacity-100" : "opacity-80"
                }`}
              />
              <img
                alt={data.sender_cp_name}
                src={
                  imageErrors[data.sender_cp_img]
                    ? data.sender_cp_gender === 0
                      ? fallbackImage1
                      : fallbackImage
                    : `${ImageBaseUrl}${data.sender_cp_img}`
                }
                onError={() => handleError(data.sender_cp_img)}
                className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 z-10 -translate-y-1/2 z-20 ${colors.height1} ${colors.width1} rounded-full object-cover border-3 border-white shadow-xl group-hover/image:scale-110 group-hover/image:border-yellow-300 transition-all duration-300`}
              />
            </div>

            {/* Middle Heart with enhanced animation */}
            <div className="relative">
              <div className="absolute inset-0 bg-pink-400/30 rounded-full blur-xl animate-pulse" />
              <img
                src={middleHeart}
                alt="heart"
                className={`relative h-10 w-10 animate-bounce-slow ${
                  rank === 1 ? "scale-110" : ""
                }`}
                style={{
                  animationDelay:
                    rank === 1 ? "0s" : rank === 2 ? "0.2s" : "0.4s",
                }}
              />
            </div>

            {/* Right Ring and Image */}
            <div className="relative group/image">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-white/20 to-transparent blur-md opacity-0 group-hover/image:opacity-100 transition-opacity duration-300" />
              <img
                src={data.receiver_cp_gender === 0 ? ringTop2 : ringTop1}
                alt="ring"
                className={`relative z-10 ${colors.height} ${colors.width} z-50 animate-spin-slow-reverse ${
                  rank === 1 ? "opacity-100" : "opacity-80"
                }`}
              />
              <img
                alt={data.receiver_cp_name}
                src={
                  imageErrors[data.receiver_cp_img]
                    ? data.receiver_cp_gender === 0
                      ? fallbackImage1
                      : fallbackImage
                    : `${ImageBaseUrl}${data.receiver_cp_img}`
                }
                onError={() => handleError(data.receiver_cp_img)}
                className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 z-10 -translate-y-1/2 z-20 ${colors.height1} ${colors.width1} rounded-full object-cover border-3 border-white shadow-xl group-hover/image:scale-110 group-hover/image:border-yellow-300 transition-all duration-300`}
              />
            </div>
          </div>

          {/* CP Type Badge with improved styling */}
          <div className="flex justify-center mt-2">
            <div
              className=" "
              style={{
                backgroundImage: `url(${TopTitle})`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                height: "28px",
                width: "100px",
              }}
            >
              <div className="flex justify-center items-center gap-1">
                <span className="text-white font-semibold text-xs">
                  {parseInt(data.exp).toLocaleString()}
                </span>
                <img
                  src={heart}
                  alt="heart"
                  className="w-7 h-7 animate-pulse"
                />
              </div>
            </div>
          </div>
          {/* CP Type Badge with improved styling */}
          <div className="flex justify-center  ">
            <div className="relative">
              <span
                style={{
                  background: "linear-gradient(135deg, #FFCB4F, #FFED75)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  textStroke: "2px #BB3800",
                  fontWeight: "bold",
                  fontSize: "1.6rem",
                }}
              >
                {rank}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };
  const renderTabContents1 = (data, tabId) => {
    if (activeTab === "tab1") {
      return (
        <>
          <div className="tab-panel pt-10 mx-auto mt-[-80px] overflow-hidden   w-[100%] flex flex-col justify-start  items-center  text-center">
            {AllEvents?.winners_at_all &&
              AllEvents.winners_at_all.length > 0 && (
                <div className="ThreeTopBg relative w-full min-h-[600px] md:min-h-[550px] py-8  overflow-hidden">
                  <div className="flex flex-col items-center gap-8">
                    {/* First Place - Centered and Larger */}
                    <div className="relative w-full max-w-lg transform hover:scale-105 transition-transform duration-500 z-20">
                      {/* Glowing background for 1st place */}
                      <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/30 to-orange-400/30 rounded-3xl blur-xl animate-pulse-slow" />

                      {/* 1st Place Card */}
                      <TopThreeCard
                        data={AllEvents.winners_at_all[0]}
                        rank={1}
                        imageErrors={imageErrors}
                        fallbackImage={fallbackImage}
                        fallbackImage1={fallbackImage1}
                        ImageBaseUrl={ImageBaseUrl}
                        handleError={handleError}
                        ringTop1={ringTop1}
                        ringTop2={ringTop2}
                        middleHeart={middleHeart}
                        topHeartBg={topHeartBg}
                        TopTitle={TopTitle}
                        heart={heart}
                      />
                    </div>

                    {/* Second and Third Place - Side by Side in same row */}
                    <div className="relative w-full flex flex-row  justify-center items-stretch gap-1   mt-4">
                      {/* Second Place */}
                      <div className="relative   md:w-[calc(50%-12px)] max-w-md transform hover:scale-105 transition-transform duration-500">
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-400/30 to-gray-500/30 rounded-3xl blur-xl animate-pulse-slow animation-delay-1000" />
                        {/* Silver medal for 2nd place */}

                        <TopThreeCard
                          data={AllEvents.winners_at_all[1]}
                          rank={2}
                          imageErrors={imageErrors}
                          fallbackImage={fallbackImage}
                          fallbackImage1={fallbackImage1}
                          ImageBaseUrl={ImageBaseUrl}
                          handleError={handleError}
                          ringTop1={ringTop11}
                          ringTop2={ringTop22}
                          middleHeart={middleHeart}
                          topHeartBg={topHeartBg2}
                          TopTitle={TopTitle2}
                          heart={heart}
                        />
                      </div>

                      {/* Third Place */}
                      <div className="relative  md:w-[calc(50%-12px)] max-w-md transform hover:scale-105 transition-transform duration-500">
                        <div className="absolute inset-0 bg-gradient-to-r from-orange-400/30 to-red-400/30 rounded-3xl blur-xl animate-pulse-slow animation-delay-2000" />
                        {/* Bronze medal for 3rd place */}

                        <TopThreeCard
                          data={AllEvents.winners_at_all[2]}
                          rank={3}
                          imageErrors={imageErrors}
                          fallbackImage={fallbackImage}
                          fallbackImage1={fallbackImage1}
                          ImageBaseUrl={ImageBaseUrl}
                          handleError={handleError}
                          ringTop1={ringTop11}
                          ringTop2={ringTop22}
                          middleHeart={middleHeart}
                          topHeartBg={topHeartBg3}
                          TopTitle={TopTitle3}
                          heart={heart}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
          </div>
          <div className=" overflow-y-scroll w-full h-[90%] mt-[35px]">
            <div className="flex flex-col justify-between w-[80%] mx-auto items-center gap-1">
              {CpTopRan && CpTopRan?.rank.length > 0 ? (
                CpTopRan?.rank.map((item, index) => (
                  <div
                    key={index}
                    className="RankBackgroundCp2 group relative  transition-all duration-500 hover:scale-105 hover:shadow-2xl"
                  >
                    {/* Animated gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/20 to-pink-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-lg" />

                    {/* Decorative floating particles for top ranks */}
                    {index < 3 && (
                      <div className="absolute inset-0 overflow-hidden rounded-lg">
                        <div className="absolute top-1 left-4 w-1 h-1 bg-yellow-300 rounded-full animate-ping opacity-50" />
                        <div className="absolute bottom-2 right-8 w-1.5 h-1.5 bg-purple-300 rounded-full animate-ping animation-delay-1000 opacity-30" />
                        <div className="absolute top-3 right-4 w-1 h-1 bg-pink-300 rounded-full animate-ping animation-delay-2000 opacity-30" />
                      </div>
                    )}

                    {/* Rank badge with special styling for top 3 */}
                    <div className="absolute  left-4 top-1/2 transform -translate-y-1/2 z-20">
                      <div
                        className={`relative ${index === 0 ? "animate-bounce-slow" : index === 1 ? "animate-pulse-slow" : ""}`}
                      >
                        {/* Glow effect for top ranks */}
                        {index < 3 && (
                          <div
                            className={`absolute inset-0 rounded-full blur-md ${
                              index === 0
                                ? "bg-yellow-400"
                                : index === 1
                                  ? "bg-gray-400"
                                  : "bg-orange-400"
                            } opacity-50 animate-pulse`}
                          />
                        )}

                        {/* Rank circle */}
                        <div
                          className={`relative w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm border-2 border-white shadow-lg transform transition-all duration-300 group-hover:scale-110 ${
                            index === 0
                              ? "bg-gradient-to-r from-yellow-400 to-yellow-600"
                              : index === 1
                                ? "bg-gradient-to-r from-gray-400 to-gray-600"
                                : index === 2
                                  ? "bg-gradient-to-r from-orange-400 to-orange-600"
                                  : "bg-gradient-to-r from-purple-500 to-pink-500"
                          }`}
                        >
                          {index + 1}
                        </div>

                        {/* Crown for 1st place */}
                      </div>
                    </div>

                    {/* Main content container */}
                    <div className="h-full z-10 w-full flex flex-row justify-center gap-5 items-center ps-6 py-2">
                      {/* Sender/Left Player */}
                      <div className="relative  w-[80px] group/player">
                        {/* Glow effect on hover */}
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 blur-md opacity-0 group-hover/player:opacity-70 transition-opacity duration-300" />

                        {/* Image container */}
                        <div className="relative">
                          <img
                            alt={item?.sender_cp_name || "Sender"}
                            src={
                              imageErrors[item?.sender_cp_img]
                                ? item?.sender_cp_gender === 0
                                  ? fallbackImage1
                                  : fallbackImage
                                : `${ImageBaseUrl}${item?.sender_cp_img}`
                            }
                            onError={() => handleError(item?.sender_cp_img)}
                            className="relative w-16 h-16 rounded-full object-cover border-3 border-white shadow-lg transform transition-all duration-500 group-hover/player:scale-110 group-hover/player:rotate-3 group-hover/player:border-yellow-300"
                          />
                          {/* Ring decoration - using sender's gender */}
                          <img
                            src={
                              item?.sender_cp_gender === 0 ? ringTop2 : ringTop1
                            }
                            alt="ring decoration"
                            className="absolute top-[-8px] left-[-5px] w-20 h-20 rounded-full object-cover pointer-events-none"
                          />
                          {/* Online indicator */}

                          {/* Player name tooltip on hover */}
                          <div className="absolute z-50 -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover/player:opacity-100 transition-opacity duration-300 whitespace-nowrap bg-black/80 text-white text-xs py-1 px-2 rounded-full pointer-events-none">
                            {item?.sender_cp_name}
                          </div>
                        </div>
                      </div>

                      {/* VS and CP Type Container */}
                      <div className="relative w-[64px] mt-[25px] flex flex-row items-center gap-1">
                        {/* CP Type with animation */}
                        <div className="relative mt-1">
                          <div className="  py-1 rounded-full text-xs font-semibold text-black shadow-lg transform transition-all duration-300 hover:scale-105  ">
                            {item?.cp_type}
                          </div>
                        </div>

                        {/* Match score or additional info (if available) */}
                        {item?.score && (
                          <div className="text-xs text-white/80 mt-1 font-medium">
                            Score: {item.score}
                          </div>
                        )}
                      </div>

                      {/* Receiver/Right Player */}
                      <div className="relative w-[80px] group/player">
                        {/* Glow effect on hover */}
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-pink-400 to-purple-400 blur-md opacity-0 group-hover/player:opacity-70 transition-opacity duration-300" />

                        {/* Image container */}
                        <div className="relative">
                          <img
                            alt={item?.receiver_cp_name || "Receiver"}
                            src={
                              imageErrors[item?.receiver_cp_img]
                                ? item?.receiver_cp_gender === 0
                                  ? fallbackImage1
                                  : fallbackImage
                                : `${ImageBaseUrl}${item?.receiver_cp_img}`
                            }
                            onError={() => handleError(item?.receiver_cp_img)}
                            className="relative w-16 h-16 rounded-full object-cover border-3 border-white shadow-lg transform transition-all duration-500 group-hover/player:scale-110 group-hover/player:-rotate-3 group-hover/player:border-yellow-300"
                          />
                          {/* Ring decoration - using receiver's gender (if available, otherwise default) */}
                          <img
                            src={
                              item?.receiver_cp_gender === 0
                                ? ringTop2
                                : ringTop1
                            }
                            alt="ring decoration"
                            className="absolute top-[-8px] left-[-5px]  w-20 h-20 rounded-full object-cover pointer-events-none"
                          />

                          {/* Player name tooltip on hover */}
                          <div className="absolute z-50 -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover/player:opacity-100 transition-opacity duration-300 whitespace-nowrap bg-black/80 text-white text-xs py-1 px-2 rounded-full pointer-events-none">
                            {item?.receiver_cp_name}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 px-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl shadow-lg animate-pulse-slow">
                  <div className="text-5xl mb-3">🏆</div>
                  <p className="text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                    {t("no data available")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      );
    } else if (activeTab === "tab2") {
      return <RewardsSection />;
    }
  };

  return (
    <div className="bg-[#F05DA3] relative overflow-hidden">
      <div className="cp h-full relative mx-auto">
        <Dialog
          isOpen={isDialogOpen}
          closeDialog={closeDialog}
          description={CpTopRan?.desc}
        />
        <img
          src={flowers}
          alt="rules"
          className=" w-full   h-full object-cover absolute top-0 left-0 opacity-20  z-0"
        />
        <div className=" w-[300px] mx-auto  "></div>
        <div className="absolute top-[38%] z-[9999] right-0">
          <div
            onClick={openDialog}
            className="relative inline-block cursor-pointer"
          >
            <img
              src={DialogButton}
              alt="rules"
              className=" w-[35px]   h-[35px]"
            />
          </div>
        </div>{" "}
      </div>
      <div className="h-full relative ">
        <div className="cpTimerBg relative -top-16">
          <div className="  mx-auto w-full   ">
            {" "}
            {CpTopRan?.time ? (
              <TimerComponent initialTime={CpTopRan?.time} />
            ) : null}{" "}
          </div>
          <div className="relative py-4">
            {/* Decorative background elements */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-10 left-1/4 w-32 h-32 bg-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-10 right-1/4 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl animate-pulse animation-delay-1000"></div>
            </div>

            {/* Main tabs container */}
            <div className="relative z-10 flex w-[95%] mx-auto items-center justify-center gap-4 sm:gap-6 md:gap-8">
              {[
                {
                  id: "tab1",
                  label: "Ranking",
                  imgActive: activeTabBar1,
                  imgInactive: tabBar1,
                  icon: "🏆",
                  color: "from-amber-400 to-yellow-500",
                },
                {
                  id: "tab2",
                  label: "Reward",
                  imgActive: activeTabBar1,
                  imgInactive: tabBar1,
                  icon: "🎁",
                  color: "from-purple-400 to-pink-500",
                },
              ].map((tab, index) => (
                <motion.div
                  key={tab.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative flex-1 max-w-[280px] cursor-pointer group`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {/* Glow effect behind the tab */}
                  <motion.div
                    animate={{
                      scale: activeTab === tab.id ? [1, 1.05, 1] : 1,
                      opacity: activeTab === tab.id ? 0.8 : 0,
                    }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className={`absolute inset-0 bg-gradient-to-r ${tab.color} rounded-2xl blur-xl -z-10`}
                  />

                  {/* Main tab container */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="relative"
                  >
                    {/* Background image with overlay */}
                    <div className="relative overflow-hidden rounded-2xl">
                      <img
                        src={
                          activeTab === tab.id ? tab.imgActive : tab.imgInactive
                        }
                        alt={tab.label}
                        className="w-full h-[45px] sm:h-[50px] object-cover"
                      />

                      {/* Animated gradient overlay on hover */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        whileHover={{ opacity: 0.2 }}
                        className={`absolute inset-0 bg-gradient-to-r ${tab.color} mix-blend-overlay`}
                      />

                      {/* Shine effect on hover */}
                      <motion.div
                        animate={{
                          x: activeTab === tab.id ? ["-100%", "200%"] : "-100%",
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: activeTab === tab.id ? Infinity : 0,
                          repeatDelay: 1,
                        }}
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"
                      />
                    </div>

                    {/* Tab content */}
                    <div className="absolute inset-0 flex items-center justify-center gap-2 px-2">
                      {/* Icon with animation */}
                      <motion.span
                        animate={{
                          rotate: activeTab === tab.id ? [0, 10, -10, 0] : 0,
                          scale: activeTab === tab.id ? [1, 1.2, 1] : 1,
                        }}
                        transition={{
                          duration: 0.5,
                          repeat: activeTab === tab.id ? Infinity : 0,
                          repeatDelay: 2,
                        }}
                        className="text-base sm:text-lg filter drop-shadow-lg"
                      >
                        {tab.icon}
                      </motion.span>

                      {/* Label with creative styling */}
                      <motion.p
                        animate={{
                          y: activeTab === tab.id ? [0, -2, 0] : 0,
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className={`font-rubik font-bold text-xs sm:text-sm md:text-base 
                ${
                  activeTab === tab.id
                    ? "text-transparent bg-clip-text bg-gradient-to-r from-white to-yellow-200"
                    : "text-white/80 group-hover:text-white"
                } transition-all duration-300 whitespace-nowrap`}
                      >
                        {t(tab.label)}
                      </motion.p>

                      {/* Active indicator dot */}
                      {activeTab === tab.id && (
                        <motion.div
                          layoutId="activeDot"
                          className="absolute -bottom-2 left-1/2 transform -translate-x-1/2"
                        >
                          <motion.div
                            animate={{
                              y: [0, -3, 0],
                              scale: [1, 1.2, 1],
                            }}
                            transition={{ duration: 1, repeat: Infinity }}
                            className="w-1.5 h-1.5 bg-white rounded-full shadow-lg shadow-white/50"
                          />
                        </motion.div>
                      )}
                    </div>

                    {/* Bottom border animation */}
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: activeTab === tab.id ? "100%" : "0%" }}
                      transition={{ duration: 0.3 }}
                      className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r ${tab.color}`}
                    />
                  </motion.div>

                  {/* Corner decorations */}
                  <div className="absolute -top-1 -left-1 w-2 h-2 border-t-2 border-l-2 border-white/30 rounded-tl-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute -top-1 -right-1 w-2 h-2 border-t-2 border-r-2 border-white/30 rounded-tr-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute -bottom-1 -left-1 w-2 h-2 border-b-2 border-l-2 border-white/30 rounded-bl-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute -bottom-1 -right-1 w-2 h-2 border-b-2 border-r-2 border-white/30 rounded-br-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </motion.div>
              ))}
            </div>

            {/* Decorative line under tabs */}
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: "80%", opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="absolute bottom-0 left-1/2 transform -translate-x-1/2 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"
            />
          </div>
        </div>
        {renderTabContents1()}
      </div>
      <div className="relative w-full loadingg group">
        {/* Animated overlay gradient on hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/10 to-pink-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg" />

        {/* Decorative floating particles */}
        <div className="absolute inset-0 overflow-hidden rounded-lg">
          <div className="absolute -top-1 -left-1 w-2 h-2 bg-yellow-300 rounded-full animate-ping opacity-50" />
          <div className="absolute top-1/2 right-4 w-1 h-1 bg-purple-400 rounded-full animate-ping animation-delay-1000 opacity-30" />
          <div className="absolute bottom-2 left-8 w-1.5 h-1.5 bg-pink-400 rounded-full animate-ping animation-delay-2000 opacity-30" />
        </div>

        {/* Sender Image with enhanced styling */}
        {CpTopRan?.my_cp?.sender ? (
          <div className="absolute left-[3.5rem] top-[1.8rem] group/image">
            {/* Glow effect */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 blur-md opacity-0 group-hover/image:opacity-70 transition-opacity duration-300" />

            {/* Image container */}
            <div className="relative">
              <img
                alt={CpTopRan?.my_cp?.sender?.uuid}
                src={
                  imageErrors[CpTopRan?.my_cp?.sender?.image]
                    ? fallbackImage1
                    : `${ImageBaseUrl}${CpTopRan?.my_cp?.sender?.image}`
                }
                onError={() => handleError(CpTopRan?.my_cp?.sender?.image)}
                className="relative w-16 h-16 rounded-full object-cover border-2 border-white shadow-lg transform transition-all duration-300 hover:scale-110 hover:border-yellow-300 hover:shadow-xl"
              />

              {/* Online status indicator */}
              <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white animate-pulse" />
            </div>
          </div>
        ) : (
          <div className="absolute left-[3.5rem] top-[0.8rem] group/image">
            <div className="absolute inset-0 rounded-full bg-gray-300 blur-md opacity-50" />
            <div className="relative">
              <img
                src={fallbackImage1}
                className="relative w-16 h-16 rounded-full object-cover border-2 border-gray-300 shadow-lg transform transition-all duration-300 hover:scale-110 hover:border-gray-400"
              />
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-gray-400 rounded-full border-2 border-white" />
            </div>
          </div>
        )}

        {/* Receiver Image with enhanced styling */}
        {CpTopRan?.my_cp?.receiver ? (
          <div className="absolute right-[3.5rem] top-[1.8rem] group/image">
            {/* Glow effect */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-pink-400 to-purple-400 blur-md opacity-0 group-hover/image:opacity-70 transition-opacity duration-300" />

            {/* Image container */}
            <div className="relative">
              <img
                alt={CpTopRan?.my_cp?.receiver?.uuid}
                src={
                  imageErrors[CpTopRan?.my_cp?.receiver?.image]
                    ? fallbackImage
                    : `${ImageBaseUrl}${CpTopRan?.my_cp?.receiver?.image}`
                }
                onError={() => handleError(CpTopRan?.my_cp?.receiver?.image)}
                className="relative w-16 h-16 rounded-full object-cover border-2 border-white shadow-lg transform transition-all duration-300 hover:scale-110 hover:border-yellow-300 hover:shadow-xl"
              />

              {/* Crown icon for top rank */}
              <div className="absolute -top-2 -right-2 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center text-xs border border-white shadow-md animate-bounce animation-delay-500">
                👑
              </div>

              {/* Online status indicator */}
              <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white animate-pulse" />
            </div>
          </div>
        ) : (
          <div className="absolute right-[3.5rem] top-[0.8rem] group/image">
            <div className="absolute inset-0 rounded-full bg-gray-300 blur-md opacity-50" />
            <div className="relative">
              <img
                src={fallbackImage}
                className="relative w-16 h-16 rounded-full object-cover border-2 border-gray-300 shadow-lg transform transition-all duration-300 hover:scale-110 hover:border-gray-400"
              />
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-gray-400 rounded-full border-2 border-white" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Design;
